import { ImportStatuses } from "utils/utils";
import { DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import dayjs from "utils/dayjs-config";

/**
 * Row shapes and the predicates derived from them, with no ag-grid, React or
 * antd in sight.
 *
 * A leaf on purpose: the grid config imports the cell renderers to name them in
 * a column def, and the renderers need these predicates to pick which icon to
 * draw. Keeping the derivations here is what stops that becoming a cycle.
 */

// Everything the grid reads lives inside the `metadata` jsonb blob, which the
// backend passes through from OPC untyped. Treat every field as optional — a
// schedule that has not finished importing arrives with a partial blob.
export type TScheduleSourceInfoMetadata = {
  name?: string;
  time?: string;
  type?: string;
  status?: string;
  dataDate?: string;
  updateDate?: string;
  description?: string;
  baselineCategoryName?: string;
};

export type TScheduleImportUser = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
};

export type TScheduleImportDetail = {
  id: string;
  is_active?: boolean | null;
  import_log?: {
    id?: string | null;
    status?: string | null;
    metadata?: Record<string, unknown> | null;
    created_by_user?: TScheduleImportUser | null;
    created_at?: string | null;
  } | null;
};

export type TScheduleSourceInfo = {
  id: string;
  name: string;
  metadata: TScheduleSourceInfoMetadata | null;
  created_by: string;
  created_at: string;
  // Status of the newest import attempt (PROCESSING | PROCESSED | FAILED), or
  // null when the schedule has never been imported. Unlike
  // schedule_import_details it also covers an attempt that failed before any
  // import row was written, so it is the one field the import state reads.
  latest_import_status?: string | null;
  // Present for every OPC schedule that has been imported into CIQ; an empty
  // array means it has not been. A schedule can be imported more than once, so
  // this is a list and the Last Imported column reads the newest entry.
  schedule_import_details?: Array<TScheduleImportDetail> | null;
};

// The API returns no ordinal, so the UI stamps one on before the rows reach the
// grid.
export type TScheduleSourceInfoRow = TScheduleSourceInfo & { srNo: number };

// The newest import wins. Ordering happens here rather than in the query because
// schedule_import_details is a nested array — the grid needs exactly one entry
// per row, and a schedule can be imported more than once.
export const getLastImport = (
  data?: TScheduleSourceInfo
): TScheduleImportDetail | undefined =>
  (data?.schedule_import_details ?? [])
    .filter((detail) => detail?.import_log?.created_at)
    .sort((a, b) =>
      String(b.import_log?.created_at).localeCompare(
        String(a.import_log?.created_at)
      )
    )[0];

export const getLatestImportStatus = (
  data?: TScheduleSourceInfo
): string | undefined => data?.latest_import_status ?? undefined;

// True while CIQ is still pulling the schedule in. Read off the row rather than
// off the tracked-import store, so an import another user started also shows as
// busy instead of offering a duplicate.
export const isScheduleImportProcessing = (
  data?: TScheduleSourceInfo
): boolean => getLatestImportStatus(data) === ImportStatuses.PROCESSING;

// Newest import ended in FAILED. Drives the failed-row tint and Actions state
// after a refetch (and on reload), alongside the in-session failure set the
// page keeps for the window before that refetch lands.
export const isScheduleImportFailed = (data?: TScheduleSourceInfo): boolean =>
  getLatestImportStatus(data) === ImportStatuses.FAILED;

// Newest import completed successfully. Outranks a stale session failure so a
// retry that landed PROCESSED does not keep the pink tint / failed icon while
// the refetch is still in flight.
export const isScheduleImportProcessed = (
  data?: TScheduleSourceInfo
): boolean => getLatestImportStatus(data) === ImportStatuses.PROCESSED;

// Imported once the newest attempt is PROCESSED, or an earlier attempt left an
// import row behind (a later failed retry does not un-import the schedule).
// Single source of truth for both the Imported column and the row action, so
// the two can never disagree about a row.
export const isScheduleImported = (data?: TScheduleSourceInfo): boolean =>
  isScheduleImportProcessed(data) ||
  Boolean(data?.schedule_import_details?.length);

/**
 * Whether the failed-row tint / failed Actions icon should show.
 *
 * Mirrors how the in-progress spinner is gated: it only shows while the import
 * is actually PROCESSING (tracked or on the row). Failed UI only shows while
 * latest_import_status is FAILED (or a session failure before that lands) —
 * any other status clears it immediately, the same way the spinner yields as
 * soon as the row is no longer processing.
 */
export const shouldShowOpcImportFailure = (
  data: TScheduleSourceInfo | undefined,
  failedImportScheduleIds: ReadonlySet<string>,
  options?: {
    isImporting?: boolean;
    /** Schedule ids whose import finished PROCESSED this session. */
    recentlyProcessedIds?: ReadonlySet<string>;
  }
): boolean => {
  if (options?.isImporting) return false;
  if (data?.id && options?.recentlyProcessedIds?.has(data.id)) return false;

  const lastStatus = getLatestImportStatus(data);
  // Newest attempt is explicitly not FAILED — drop failed UI at once (PROCESSED,
  // PROCESSING, or any later status), including a stale session failure entry.
  if (lastStatus && lastStatus !== (ImportStatuses.FAILED as string)) {
    return false;
  }

  return (
    Boolean(data?.id && failedImportScheduleIds.has(data.id)) ||
    lastStatus === (ImportStatuses.FAILED as string)
  );
};

// import_log.created_at is a real instant from CIQ's own database, not one of
// OPC's mangled strings — so it is read in the project timezone rather than
// through formatOpcDate, whose .utc() branch exists only because OPC's dataDate
// is a UTC-midnight stand-in for a calendar date.
export const formatImportTimestamp = (value?: string | null): string => {
  if (!value) return "";
  const parsed = dayjs(value).tz();
  return parsed.isValid() ? parsed.format(DATE_FORMAT_MMDDYYYY) : "";
};

// Who ran the newest import, as "First Last". Empty when the import_log has not
// resolved or the user has since been removed — either half can be absent, and
// half a name still names someone.
export const getLastImportedByName = (data?: TScheduleSourceInfo): string => {
  const user = getLastImport(data)?.import_log?.created_by_user;
  return [user?.first_name, user?.last_name]
    .filter((part) => part && part.trim().length)
    .join(" ");
};

/**
 * The import_log id of a schedule's newest import — the id Change Impact keys
 * its version dropdown on, and the one every other deep link into that page
 * reads (feature-schedule-details' "Compare with active version" takes it from
 * the same field).
 *
 * Deliberately NOT the id the export POST hands back. That names the job, and
 * the two only coincide if the backend files the schedule under the same log;
 * an id that names no version leaves the dropdown rendering a raw uuid and the
 * impact endpoint reading S3 with no key.
 */
export const getLastImportLogId = (
  data?: TScheduleSourceInfo
): string | undefined => getLastImport(data)?.import_log?.id ?? undefined;

// The import CIQ is currently running the project off. Any one of the schedule's
// imports being active makes the schedule active — `.some` rather than reading
// the newest, because activation follows what the project is actually using, not
// what arrived last: a newer import can sit inactive behind an older, active one.
export const isScheduleImportActive = (data?: TScheduleSourceInfo): boolean =>
  (data?.schedule_import_details ?? []).some(
    (detail) => detail?.is_active === true
  );

// Sent to OPC as the name to import under, and what ends up on the CIQ schedule.
export const OPC_IMPORT_FALLBACK_SCHEDULE_NAME = "Untitled OPC Schedule";

// metadata.name first: that is what the grid's Name column renders, so it is the
// string the user just confirmed against. Never blocks — an OPC schedule cannot
// be renamed from CIQ, so "this one has no name" would be a dead end.
export const getImportScheduleName = (data?: TScheduleSourceInfo): string =>
  data?.metadata?.name?.trim() ||
  data?.name?.trim() ||
  OPC_IMPORT_FALLBACK_SCHEDULE_NAME;
